import React, { useEffect, useState } from 'react';
import * as Sentry from "@sentry/react";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  signOut
} from 'firebase/auth';
import { doc, getDoc, getDocFromCache, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { isOfflineError } from './lib/firestore';
import { trackEvent } from './lib/analytics.js';

/**
 * Fetches a Firestore document, falling back to the local cache when offline.
 * Returns the snapshot (possibly from cache), or rethrows for non-offline errors.
 */
// Read a doc, tolerating the transient "client is offline" / unavailable state
// that Firestore can report right after an external redirect (e.g. returning
// from Stripe Checkout) before its connection is ready. On such errors we try
// the local cache, then briefly back off and retry the live read a few times so
// the user is not bounced to the offline screen for a momentary hiccup.
async function getDocWithCacheFallback(ref, { retries = 3, delayMs = 400 } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await getDoc(ref);
    } catch (error) {
      lastError = error;
      if (!isOfflineError(error)) throw error;
      try {
        return await getDocFromCache(ref);
      } catch {
        // No cached copy yet; wait (growing backoff) and retry the live read.
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
    }
  }
  throw lastError;
}

export default function AuthGate({ children, surface }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingAllowlist, setIsCheckingAllowlist] = useState(false);
  const [allowlistError, setAllowlistError] = useState(null);

  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showRedirectFallback, setShowRedirectFallback] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState(null);
  const [inviteStatus, setInviteStatus] = useState('idle');
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (!user) {
      trackEvent('app_auth_view', { surface });
    }
  }, [user, surface]);

  useEffect(() => {
    // Handle Redirect Result
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          console.log('Redirect sign-in successful');
          trackEvent('login', { surface, method: 'google' });
        }
      })
      .catch((error) => {
        console.error('Error getting redirect result', error);
        Sentry.captureException(error, { tags: { action: 'getRedirectResult' } });
        setAuthError('リダイレクトログインに失敗しました。');
      })
      .finally(() => {
        // If we were waiting for a redirect result, stop loading
        // Note: onAuthStateChanged will also set isLoading(false) if user is detected
      });

    // Handle Email Link Sign In
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let savedEmail = window.localStorage.getItem('secondBrain.emailForSignIn');
      if (!savedEmail) {
        savedEmail = window.prompt('確認のため、ログインに使用したメールアドレスを入力してください');
      }

      if (savedEmail) {
        // isLoading is already true initially
        signInWithEmailLink(auth, savedEmail, window.location.href)
          .then(() => {
            trackEvent('login', { surface, method: 'email_link' });
            window.localStorage.removeItem('secondBrain.emailForSignIn');
            // Remove the auth params from the URL so they don't get reused
            window.history.replaceState(null, '', window.location.pathname);
          })
          .catch((error) => {
            console.error('Error signing in with email link', error);
            setAuthError('リンクが無効か、期限切れです。もう一度お試しください。');
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        // User cancelled prompt. Use setTimeout to avoid synchronous setState warning
        setTimeout(() => setIsLoading(false), 0);
      }
    } else {
      // Not an email link sign in, let auth state change handle loading state
    }
  }, [surface]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        Sentry.setUser({ id: currentUser.uid });
        setIsCheckingAllowlist(true);
        setAllowlistError(null);
        setAccessDeniedReason(null);
        try {
          const uid = currentUser.uid;
          const userEmail = currentUser.email;

          // Check if users/{uid} exists
          const userRef = doc(db, 'users', uid);
          const userSnap = await getDocWithCacheFallback(userRef);

          let finalRole = null;

          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.status !== 'active') {
              const reason = 'inactive_user';
              setAllowlistError('アクセス権が保留中または無効化されています。管理者に連絡してください。');
              setAccessDeniedReason(reason);
              trackEvent('app_access_denied', { surface, reason_type: reason });
              setUser(null);
            } else {
              const productRoles = userData.productRoles || {};
              finalRole = userData.role === 'owner' ? 'owner' : (productRoles.sink || userData.role || 'member');
              setUser(Object.assign(currentUser, {
                role: finalRole,
                status: userData.status,
                productRoles: productRoles
              }));
            }
          } else {
            // New user registration: check trialInvites/{email}
            const normalizedEmail = userEmail ? userEmail.toLowerCase() : '';
            if (normalizedEmail) {
              const inviteRef = doc(db, 'trialInvites', normalizedEmail);
              const inviteSnap = await getDocWithCacheFallback(inviteRef);

              if (inviteSnap.exists()) {
                const inviteData = inviteSnap.data();
                if (inviteData.status !== 'active') {
                  const reason = 'inactive_invite';
                  setAllowlistError('招待が保留中または無効化されています。');
                  setAccessDeniedReason(reason);
                  trackEvent('app_access_denied', { surface, reason_type: reason });
                  setUser(null);
                } else {
                  // Create users/{uid} from trialInvites data
                  const newRole = inviteData.role || 'credit_beta';
                  const newUserDoc = {
                    email: userEmail,
                    status: 'active',
                    role: newRole,
                    grantedCredits: 100,
                    usedCredits: 0,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                  };
                  await setDoc(userRef, newUserDoc);
                  finalRole = newRole;
                  setUser(Object.assign(currentUser, {
                    role: finalRole,
                    status: 'active',
                    productRoles: { sink: 'member' } // Default for new provisioned users
                  }));
                }
              } else {
                const reason = 'no_invite';
                setAllowlistError('アクセス権がありません。招待をお待ちください。');
                setAccessDeniedReason(reason);
                setInviteStatus('idle');
                trackEvent('app_access_denied', { surface, reason_type: reason });
                setUser(null);
              }
            } else {
              setAllowlistError('メールアドレスが取得できませんでした。');
              setAccessDeniedReason('missing_email');
              setUser(null);
            }
          }

        } catch (error) {
          console.error('Error checking allowlist:', error);
          if (isOfflineError(error)) {
            setAllowlistError('オフラインのため、アクセス権を確認できませんでした。インターネット接続を確認してから再度お試しください。');
            setAccessDeniedReason('offline');
          } else {
            Sentry.captureException(error, { tags: { action: 'checkAllowlist' } });
            setAllowlistError('アクセス権の確認中にエラーが発生しました。');
            setAccessDeniedReason('check_error');
          }
          setUser(null);
        } finally {
          setIsCheckingAllowlist(false);
          setIsLoading(false);
        }
      } else {
        Sentry.setUser(null);
        setUser(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [surface]);

  const handleGoogleSignIn = async () => {
    setAuthError('');
    setShowRedirectFallback(false);
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      trackEvent('login', { surface, method: 'google' });
    } catch (error) {
      console.error('Google Sign In Error', error);
      if (error.code === 'auth/popup-blocked') {
        setShowRedirectFallback(true);
        setAuthError('ブラウザによってポップアップがブロックされました。リダイレクトログインを試してください。');
      } else if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        setShowRedirectFallback(true);
        setAuthError('Googleログインのポップアップが完了前に閉じました。ブラウザ設定や拡張機能の影響が考えられます。リダイレクトログインを試してください。');
      } else {
        Sentry.captureException(error, { tags: { action: 'handleGoogleSignIn' } });
        setAuthError(`Googleログインに失敗しました。${error.code ? ` (${error.code})` : ''}`);
      }
      setIsLoading(false);
    }

  };

  const handleGoogleSignInRedirect = async () => {
    setAuthError('');
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithRedirect(auth, provider);
    } catch (error) {
      console.error('Google Redirect Sign In Error', error);
      Sentry.captureException(error, { tags: { action: 'handleGoogleSignInRedirect' } });
      setAuthError('リダイレクトログインの開始に失敗しました。');
      setIsLoading(false);
    }
  };

  const handleEmailLinkSignIn = async (e) => {
    e.preventDefault();
    if (!email) return;

    setAuthError('');
    setIsLoading(true);

    const actionCodeSettings = {
      url: window.location.href, // Redirect back to the same page
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('secondBrain.emailForSignIn', email);
      setEmailSent(true);
      trackEvent('login_link_send', { surface, method: 'email_link' });
    } catch (error) {
      console.error('Email Link Sign In Error', error);
      Sentry.captureException(error, { tags: { action: 'handleEmailLinkSignIn' } });
      setAuthError('メール送信に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    // Clear the current user's local storage keys before signing out
    if (auth.currentUser) {
      window.localStorage.removeItem(`secondBrain.${auth.currentUser.uid}.sinkRoomId`);
    }
    Sentry.setUser(null);
    signOut(auth);
    setAllowlistError(null);
    setAccessDeniedReason(null);
    setInviteStatus('idle');
    setInviteError('');
  };

  const handleInviteRegistration = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || inviteStatus === 'loading') return;

    setInviteStatus('loading');
    setInviteError('');
    try {
      await setDoc(doc(db, 'invited', currentUser.uid), {
        email: currentUser.email || '',
        displayName: currentUser.displayName || '',
        photoURL: currentUser.photoURL || '',
        sinkRequestedAt: serverTimestamp(),
      }, { merge: true });
      setInviteStatus('done');
      trackEvent('invite_registration_complete', { surface, source_app: 'sink' });
    } catch (error) {
      console.error('Invite registration failed:', error);
      Sentry.captureException(error, { tags: { action: 'handleInviteRegistration' } });
      setInviteStatus('error');
      setInviteError('登録できませんでした。時間をおいてもう一度お試しください。');
      trackEvent('invite_registration_error', { surface, source_app: 'sink' });
    }
  };

  if (isLoading || isCheckingAllowlist) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (allowlistError) {
    // Offline/transient: this is not a real access denial, so keep the framing
    // soft (no "保留中"/denied wording) and let the user simply re-open the app.
    if (accessDeniedReason === 'offline') {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white p-4">
          <div className="bg-gray-800 p-8 rounded-2xl border border-gray-700 max-w-md w-full text-center shadow-xl">
            <h2 className="text-xl font-bold mb-3">接続を確認しています</h2>
            <p className="text-gray-300 text-sm mb-6">
              ネットワークの確認に少し時間がかかっています。下のボタンからアプリを開き直してください。
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold transition"
            >
              アプリに戻る
            </button>
          </div>
        </div>
      );
    }
    const invitedEmail = auth.currentUser?.email || '';
    const canRegisterInvite = accessDeniedReason === 'no_invite' && Boolean(auth.currentUser);
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white p-4">
        <div className="bg-gray-800 p-8 rounded-2xl border border-gray-700 max-w-md w-full text-center shadow-xl">
          <h2 className="text-2xl font-bold mb-4">アクセス保留中</h2>
          <p className="text-gray-300 mb-6">{allowlistError}</p>
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-400">現在のアカウント:</p>
            <p className="font-medium">{auth.currentUser?.email}</p>
          </div>
          {canRegisterInvite && inviteStatus === 'done' ? (
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm font-bold text-emerald-200 mb-1">招待リストに登録しました</p>
              <p className="text-xs text-emerald-100/80">
                {invitedEmail} 宛に案内できる状態になったらお知らせします。
              </p>
            </div>
          ) : canRegisterInvite ? (
            <button
              type="button"
              onClick={handleInviteRegistration}
              disabled={inviteStatus === 'loading'}
              className="w-full px-6 py-3 mb-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white rounded-lg font-bold transition"
            >
              {inviteStatus === 'loading' ? '登録中...' : '招待リストに登録する'}
            </button>
          ) : null}
          {canRegisterInvite && inviteError && (
            <p className="text-xs text-rose-300 mb-3">{inviteError}</p>
          )}
          <button
            onClick={handleSignOut}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition"
          >
            別のアカウントでログイン
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white p-4">
        <div className="bg-gray-800 p-8 rounded-2xl border border-gray-700 max-w-md w-full shadow-xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Second Brain</h1>
            <p className="text-gray-400 text-sm">プロジェクトメンバー専用トライアル</p>
          </div>

          {authError && (
            <div className="mb-6 p-3 bg-rose-900/30 border border-rose-500/30 rounded-lg text-rose-300 text-sm text-center">
              {authError}
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-3">
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-4 rounded-xl transition"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Googleでログイン
              </button>

              {showRedirectFallback && (
                <button
                  onClick={handleGoogleSignInRedirect}
                  className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-xl transition"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
                  </svg>
                  Googleリダイレクトログインを試す
                </button>
              )}
            </div>

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-gray-700"></div>
              <span className="flex-shrink-0 mx-4 text-gray-500 text-sm">または</span>
              <div className="flex-grow border-t border-gray-700"></div>
            </div>

            {emailSent ? (
              <div className="text-center p-4 border border-indigo-500/30 bg-indigo-900/20 rounded-xl">
                <p className="text-indigo-200 font-medium mb-2">リンクを送信しました</p>
                <p className="text-sm text-gray-400">
                  {email} 宛にログイン用のリンクを送信しました。メールをご確認ください。
                </p>
                <button
                  onClick={() => setEmailSent(false)}
                  className="mt-4 text-sm text-indigo-400 hover:text-indigo-300"
                >
                  別のメールアドレスを使う
                </button>
              </div>
            ) : (
              <form onSubmit={handleEmailLinkSignIn} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="メールアドレス"
                    required
                    className="w-full bg-gray-900 border border-gray-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>
                <button
                  type="submit"
                  disabled={!email || isLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold py-3 px-4 rounded-xl transition"
                >
                  メールリンクでログイン
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Children can access user via props or context in real apps, but we'll pass it to children explicitly
  // if children is a single React element, we can clone it and pass the user prop
  return React.cloneElement(children, { user, signOut: handleSignOut });
}
